import type { SpeechType } from './NormalEdge';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { fonts } from '@theme/fontsCustomer';
import IFPathIcon from '@icons/IFPathIcon';
import { PlayIcon } from '@icons/PlayIcon';

interface SelectNodeSpeechProps {
  menuPosition: { x: number; y: number };
  onClickSpeech: (e: React.MouseEvent, type: SpeechType) => void;
}

export const SelectNodeSpeech = ({ menuPosition, onClickSpeech }: SelectNodeSpeechProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return createPortal(
    <MenuPortal style={{ left: `${menuPosition.x}px`, top: `${menuPosition.y}px` }}>
      <MenuWrapper>
        <MenuContainer>
          <MenuOption onClick={e => onClickSpeech(e, 'ACTION')}>
            <PlayIcon width={28} height={28} color={theme.colors['ic-gray-dark']} />
            <MenuText>{t('Workflow_Create_77')}</MenuText>
          </MenuOption>
          <MenuOption onClick={e => onClickSpeech(e, 'CONDITION')}>
            <IFPathIcon width={28} height={28} color={theme.colors['ic-gray-dark']} />
            <MenuText>{t('Workflow_Create_78')}</MenuText>
          </MenuOption>
        </MenuContainer>
      </MenuWrapper>
    </MenuPortal>,
    document.body,
  );
};

const MenuPortal = styled.div`
  position: fixed;
  z-index: 99999;
  pointer-events: auto;
`;

const MenuWrapper = styled.div`
  position: relative;
  width: 272px;
  pointer-events: auto;

  /* 화살표 */
  &::before {
    content: '';
    position: absolute;
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-bottom: 9px solid ${props => props.theme.colors['border-gray-light']};
  }

  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${props => props.theme.colors['bg-white']};
  }
`;

const MenuContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 272px;
  height: 122px;
  padding: 22px 16px;
  box-sizing: border-box;
  background: ${props => props.theme.colors['bg-white']};
  border: 1px solid ${props => props.theme.colors['border-gray-light']};
  border-top: none;
  border-radius: 4px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);

  /* 상단 border - 화살표 좌측 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: calc(50% - 9px);
    height: 0;
    border-top: 1px solid ${props => props.theme.colors['border-gray-light']};
  }

  /* 상단 border - 화살표 우측 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: calc(50% - 9px);
    height: 0;
    border-top: 1px solid ${props => props.theme.colors['border-gray-light']};
  }
`;

const MenuOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 114px;
  height: 84px;
  padding: 16px 0;
  box-sizing: border-box;
  background: ${props => props.theme.colors['bg-gray-lighter']};
  border: 1px solid ${props => props.theme.colors['border-gray-light']};
  border-radius: 3px;
  cursor: pointer;

  :hover {
    background: ${props => props.theme.colors['bg-primary-lighter']};
    border-color: ${props => props.theme.colors['border-primary-light']};
  }
`;

const MenuText = styled.span`
  ${fonts.Headline8};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;
